import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Button,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	StaffFilter,
	StaffListSummaryItem,
} from '@/api/DTO/staff/staffList.interface';
import StaffListSummaryFilterSection from './components/StaffListSummaryFilterSection';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { useGetStaffPageQuery } from '@/api/staff/staffApiSlice';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { useGetUser, useGetUserResultType } from '@/hooks/useGetUser';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { UserRole } from '@/api/enum/userRole.enum';
import { showLabel } from '@/utils/helper';
import AppDataGridHeaderWithSorting from '@/common/components/dataGrid/AppDataGridHeaderWithSorting';

export interface staffListDataGrid {
	lastLogin: string;
	createdAt: string;
}

export default function StaffListSummaryPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const staffList = [
		{ name: t('common.admin'), id: UserRole.ROLE_WEB_ADMIN },
		{ name: t('common.staff'), id: UserRole.ROLE_WEB_STAFF },
	];

	const { userRoles } = useGetUser<useGetUserResultType>({
		roleObj: {
			create: ['create', 'staffList'],
		},
	});

	const defaultPropertyFilter: StaffFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		roles: [],
	};
	const defaultValues: DefaultValues<StaffFilter> = defaultPropertyFilter;
	const methods = useForm<StaffFilter>({
		defaultValues,
	});
	const transformedSearchParams: StaffFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'roles',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetStaffPageQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: StaffListSummaryItem[]) => {
		if (response == null) return;
		const newResponse: staffListDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				lastLogin: !item.lastLogin
					? '-'
					: dayjs(item.lastLogin)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as StaffFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const DataGridHeader = (params: GridColumnHeaderParams) => {
		return (
			<AppDataGridHeaderWithSorting
				headerName={params.colDef.headerName}
				sortingsPropertyName={params.colDef.field}
			/>
		);
	};

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t('staff.staffName'),
			flex: 21,
			renderHeader: DataGridHeader,
		},
		{
			field: 'email',
			headerName: t('staff.email'),
			flex: 15,
		},
		{
			field: 'createdAt',
			headerName: t('staff.createTime'),
			flex: 21,
			renderHeader: DataGridHeader,
		},
		{
			field: 'lastLogin',
			headerName: t('staff.lastOnlinetime'),
			flex: 21,
			renderHeader: DataGridHeader,
		},
		{
			field: 'role',
			headerName: t('staff.permission'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.role == UserRole.ROLE_WEB_ADMIN
							? 'chipPrimary'
							: 'default'
					}
					label={showLabel(params.row.role, staffList)}
				/>
			),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/staff-manage/staff-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.staffList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					{userRoles.create && (
						<Button
							variant='contained'
							endIcon={<PersonAddIcon />}
							onClick={() =>
								navigate(`/staff-manage/staff-list/add`, {
									state: {
										propertySummarySearchParams: qs.stringify(
											transformedSearchParams
										),
									},
								})
							}
							style={{ alignItems: 'center' }}
						>
							{t('button.create')}
						</Button>
					)}
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
